import React, { useState, useMemo, useCallback } from 'react';
import * as CONSTANTS from "common/utils/data_validations"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Form, Label, Row, Col } from 'reactstrap';
import Select from 'react-select';
import { PayloadEditor } from 'components/payload_editor/PayloadEditor';
import { getOptionFromValueForSelectBox, getPrimaryButtonClass } from 'helpers/sf_utils';
import { INTEGRATION_SAMPLE_LEAD_PRODUCT_OPTIONS, MODE, REQUEST_CONTENT_TYPE, REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getLeadIntegrationDefaultPayloadValue } from 'store/integration/actions';
import useClearableTimeout from 'hooks/useClearableTimeout';
import { useEffect } from 'react';
import SfLoadingSkeleton from 'components/Common/Loadings/SfLoadingSkeleton';
import LabelWithTooltip from 'components/Common/LabelWithTooltip';
import useFilteredIntegrationLeadTypes from 'hooks/useFilteredIntegrationLeadTypes';
import useFeatureFlags from 'hooks/useFeatureFlags';


const optionsContentType = [{
  options: [
    { label: REQUEST_CONTENT_TYPE.JSON, value: REQUEST_CONTENT_TYPE.JSON },
    { label: REQUEST_CONTENT_TYPE.XML, value: REQUEST_CONTENT_TYPE.XML },
    { label: REQUEST_CONTENT_TYPE.SOAP, value: REQUEST_CONTENT_TYPE.SOAP },
    { label: REQUEST_CONTENT_TYPE.HTTP, value: REQUEST_CONTENT_TYPE.HTTP },
  ]
}];

const TOOLTIP_CONSTANTS = {
  CONTENT_TYPE: {
    ID: "api_fields_tooltip_content_type",
    NAME: "Content Type",
    TOOLTIP_MESSAGE: "The Content-Type representation header is used to indicate the original media type of the resource"
  },
  LEAD_TYPE: {
    ID: "api_fields_tooltip_lead_type",
    NAME: "Lead Type",
    TOOLTIP_MESSAGE: "The type of insurance lead"
  },
  COPY_PAYLOAD_TO: {
    ID: "api_fields_tooltip_copy_payload_to",
    NAME: "Copy Payload To",
    TOOLTIP_MESSAGE: "Allows you to copy the payload of one lead type to another"
  }
}

// readOnly (BM-9524): View without Manage. Lead-type, macro browsing and sample
// payload stay usable so the payload can be inspected; anything that edits or
// saves is disabled.
const ApiFields = ({ mode, initialState, onSubmitState, sf_lead_integration_macros, sf_lead_integration_macro_categories, openNextAccordion, updateActiveLeadTypeId, readOnly = false }) => {

  const [parsedCodes, setParsedCodes] = useState(initialState?.lead_integration?.lead_integration_req_payloads)

  useEffect(() => {
    updateActiveLeadTypeId(parseInt(initialState?.lead_integration?.ui_lead_type_id || 6))
  }, [])

  const { integration_id } = useParams();
  const dispatch = useDispatch();
  const [otpTimeoutSet] = useClearableTimeout();
  const { leadTypes, copyLeadPayloadDropdownOptions } = useFilteredIntegrationLeadTypes();
  const [isFeatureFlagActive] = useFeatureFlags();
  const productSpecificSampleLeadsEnabled = isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_PRODUCT_SPECIFIC_SAMPLE_LEADS);
  const [selectedProductType, setSelectedProductType] = useState(INTEGRATION_SAMPLE_LEAD_PRODUCT_OPTIONS[0]);

  const handleUpdateMacroMapped = (lead_integration_macros, set_loading) => {
    if (readOnly) {
      return;
    }
    const lead_integration = {
      lead_integration_macros: lead_integration_macros,
    }

    if (mode === MODE.EDIT) {
      onSubmitState({ lead_integration }, set_loading)
    }

    otpTimeoutSet(() => {
      dispatch(getLeadIntegrationDefaultPayloadValue({
        id: integration_id,
        product_type_id: productSpecificSampleLeadsEnabled ? selectedProductType?.value : undefined,
      }))
    }, 2000)
  }

  useEffect(() => {
    if (mode !== MODE.EDIT || !integration_id || !productSpecificSampleLeadsEnabled) {
      return;
    }

    dispatch(getLeadIntegrationDefaultPayloadValue({
      id: integration_id,
      product_type_id: selectedProductType?.value,
    }))
  }, [dispatch, integration_id, mode, productSpecificSampleLeadsEnabled, selectedProductType?.value])

  /**
   * 
   * @param {*} values 
   * @param {*} lead_type_id 
   */
  const handleLeadTypeCopyTo = (selectedOption, lead_type_id) => {
    setParsedCodes((prevState) => {
      const data = prevState.find(prev => prev.lead_type_id === lead_type_id)?.data || ""
      return prevState.map((prev => {
        if (prev.lead_type_id === selectedOption.value) {
          return { ...prev, data }
        } else {
          return prev
        }
      }))
    });
  }

  const { sample_leads, lead_integration_loading } = useSelector(state => ({
    sample_leads: state.Integration.lead_integration_default_payload_value,
    lead_integration_loading: state.Integration.loading,
  }))

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      lead_type_id: parseInt(initialState?.lead_integration?.ui_lead_type_id) || 6,
      lead_type_copy_to: initialState.lead_type_copy_to || null,
      req_content_type: getOptionFromValueForSelectBox(optionsContentType[0].options, initialState?.lead_integration?.req_content_type) || null,
    },
    validationSchema: Yup.object({
      req_content_type: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
    }),
    onSubmit: (values, { resetForm }) => {
      const lead_integration = {
        req_content_type: values?.req_content_type?.value || "",
        lead_integration_macros: initialState?.lead_integration?.lead_integration_macros || [],
        lead_integration_req_payloads: parsedCodes,
        ui_lead_type_id: values?.lead_type_id
      }

      if (mode === MODE.EDIT) {
        onSubmitState({ lead_integration }, true)
      }
      if (mode === MODE.CREATE) {
        onSubmitState({ ...initialState, lead_integration: { ...initialState?.lead_integration, ...lead_integration } }, true);
      }
      openNextAccordion();
      resetForm();
    },
  });

  useEffect(() => {
    setParsedCodes(prevParsedCodes => {
      // For first iternation, lead types is not loaded yet
      // In that case, return previous parsed codes as is
      if (!leadTypes || leadTypes.length === 0) {
        return prevParsedCodes;
      }

      // Get all lead type IDs that should be present
      const requiredLeadTypeIds = leadTypes.map(lt => lt.id);
      
      // Create a map of existing codes for quick lookup
      const existingCodesMap = new Map();
      prevParsedCodes.forEach(code => {
        existingCodesMap.set(code.lead_type_id, code);
      });
      
      // Ensure all required lead types are present
      const updatedCodes = requiredLeadTypeIds.map(leadTypeId => {
        if (existingCodesMap.has(leadTypeId)) {
          return existingCodesMap.get(leadTypeId);
        } else {
          return {
            lead_type_id: leadTypeId,
            data: ''
          };
        }
      });

      return updatedCodes;
    });
  }, [leadTypes])

  const parsedCode = useMemo(() => {
    return (parsedCodes || []).find(code => code.lead_type_id == validation?.values?.lead_type_id)?.data
  }, [parsedCodes, validation?.values?.lead_type_id]);

  const setParsedCode = useCallback((code) => {
    setParsedCodes(prev => (
      (prev || []).map(el => el.lead_type_id == validation.values.lead_type_id ? ({ ...el, data: code }) : el)
    ))
  }, [validation?.values?.lead_type_id]);

  if (lead_integration_loading) {
    return <SfLoadingSkeleton rows={4} cols={1} />
  }

  return (
    <React.Fragment>
      <Form
        onSubmit={e => {
          e.preventDefault()
          if (!readOnly) {
            validation.handleSubmit()
          }
          return false
        }}
      >
        <Row>
          <Col md={12} xl={12}>
            <div className="w-25 mb-3">
              <LabelWithTooltip name={TOOLTIP_CONSTANTS.CONTENT_TYPE.NAME} tooltip_message={TOOLTIP_CONSTANTS.CONTENT_TYPE.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.CONTENT_TYPE.ID} htmlFor="name" />
              <Select
                options={optionsContentType}
                isDisabled={readOnly}
                classNamePrefix="select2-selection"
                onChange={option => {
                  let event = {
                    target: {
                      name: "req_content_type",
                      value: option,
                    },
                  }
                  validation.handleChange(event)
                }}
                onBlur={() => {
                  validation.handleBlur({
                    target: { name: "req_content_type" },
                  })
                }}
                value={validation.values.req_content_type}
                invalid={
                  validation.touched.req_content_type &&
                    validation.errors.req_content_type
                    ? true
                    : false
                }
              />
              {validation.touched.req_content_type &&
                validation.errors.req_content_type ? (
                <span className="text-danger">
                  {CONSTANTS.STR_REQUIRED_FIELD}
                </span>
              ) : null}
            </div>
          </Col>
          <Col md={12} xl={12}>
            <div className="mb-3">
              <LabelWithTooltip name={TOOLTIP_CONSTANTS.LEAD_TYPE.NAME} tooltip_message={TOOLTIP_CONSTANTS.LEAD_TYPE.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.LEAD_TYPE.ID} htmlFor="name" />
              <div className="d-flex flex-wrap gap-3">
                {leadTypes.map(single_type => {
                  return (
                    <div className='campaign-type text-center' key={single_type.id}>
                      <input
                        type="radio"
                        className="btn-check"
                        name="lead_type_id"
                        value={single_type.id}
                        onClick={(e) => {
                          const event = {
                            ...e,
                            target: {
                              value: single_type.id,
                              name: 'lead_type_id',
                            },
                          }
                          validation.handleChange(event)
                          updateActiveLeadTypeId(single_type.id)
                        }}
                        id={`${single_type.name}_${single_type.id}`}
                        defaultChecked={validation?.values?.lead_type_id == single_type.id}
                        autoComplete="off"
                      />
                      <Label className="btn text-capitalize"
                        htmlFor={`${single_type.name}_${single_type.id}`}>
                        {single_type.name}
                      </Label>
                    </div>
                  )
                })
                }
              </div>
            </div>
          </Col>

          <Col md={12} xl={12}>
            <PayloadEditor
              req_payload_type={validation?.values?.req_content_type?.value}
              parsedCode={parsedCode}
              updateParsedCode={setParsedCode}
              sf_lead_integration_macros={sf_lead_integration_macros}
              sf_lead_integration_macro_categories={sf_lead_integration_macro_categories}
              macroMappingState={initialState?.lead_integration?.lead_integration_macros || []}
              updateMacroMapped={handleUpdateMacroMapped}
              sample_leads={sample_leads}
              lead_type_id={validation?.values?.lead_type_id || null}
              productSpecificSampleLeadsEnabled={productSpecificSampleLeadsEnabled}
              productOptions={INTEGRATION_SAMPLE_LEAD_PRODUCT_OPTIONS}
              selectedProductType={selectedProductType}
              onSelectedProductTypeChange={setSelectedProductType}
              readOnly={readOnly}
            />
          </Col>

          <Col md={12} xl={12}>
            <div className="w-25 mb-3 mt-4">
              <LabelWithTooltip name={TOOLTIP_CONSTANTS.COPY_PAYLOAD_TO.NAME} tooltip_message={TOOLTIP_CONSTANTS.COPY_PAYLOAD_TO.TOOLTIP_MESSAGE} target={TOOLTIP_CONSTANTS.COPY_PAYLOAD_TO.ID} htmlFor="name" />
              <Select
                options={copyLeadPayloadDropdownOptions.filter(lt => lt.value != validation?.values?.lead_type_id)}
                isDisabled={readOnly}
                classNamePrefix="select2-selection"
                onChange={selectedOption => {
                  let event = {
                    target: {
                      name: "lead_type_copy_to",
                      value: selectedOption,
                    },
                  }
                  validation.handleChange(event)
                  handleLeadTypeCopyTo(selectedOption, validation?.values?.lead_type_id);
                }}
                value={validation.values.lead_type_copy_to}
              />
            </div>
          </Col>

          <Col md={12} xl={12}>
            <div className="my-3 flex-container space-between">
              <button className={`btn ${getPrimaryButtonClass()} flex-item`} type="submit" name="submit" disabled={readOnly}> {false ? "Saving" : (mode === MODE.EDIT ? "Save" : "Continue")}</button>
              <button hidden={false ? false : true} className="btn btn-light ms-3 flex-item" type="button" onClick={validation.resetForm} name="cancel">Cancel</button>
            </div>
          </Col>
        </Row>

      </Form>
    </React.Fragment>
  )
}

export default ApiFields
